import {
  Alert,
  AlertTitle,
  Box,
  Collapse,
  Link,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { getFlagStateValue } from '../../../../experimentation/base';
import { useExperimentation } from '../../../../experimentation/client';
import {
  InsurancePayerPlanMap,
  PatientPrimaryPolicyholderInput,
  useGetCarriersQuery,
  useGetInsurancePayersForAssessmentQuery,
  useGetInsurancePlansForAssessmentLazyQuery,
  useGetPlansLazyQuery,
} from '../../../../generated/graphql';
import {
  MinorPolicyHolderRelationLabelValueMap,
  PolicyHolderRelationLabelValueMap,
  createPolicyholderObjectHelper,
} from '../../../../helpers/assessment';
import { useAppDispatch } from '../../../../pages/assessment';
import {
  selectMedicalInfoAnswers,
  selectPatientGuardians,
  updateInsuranceForm,
  updatePrimaryPolicyholder,
} from '../../../../redux/assessment';
import { selectLdFlags } from '../../../../redux/ld-flags';
import { selectIsGuardian } from '../../../../redux/session';
import {
  DispatchUpdateAssessmentInput,
  Screens,
} from '../../../../redux/types';
import { rules } from '../../demographics/rules';
import { AssessmentButtons, AssessmentCard } from '../../shared';
import { ControlledAutocomplete } from '../../shared/controlled-autocomplete';
import { DialogBox } from '../../shared/dialog-box';
import { PrimaryPolicyHolderFormValues } from '../primary-policyholder';
import { PrimaryPolicyHolderForm } from '../primary-policyholder/form';
import InsuranceLogoGrid from './insurance-logo-grid';
import InsuranceUsageInfoModal from './insurance-usage-info-modal';
import MoreInsuranceInfoForm from './more-info-form';

export type Carrier = {
  label: string;
  value: string;
};

export type Plan = {
  label: string;
  value: string;
  inNetwork: boolean;
  isReferralNeeded: boolean;
  isOutofStateNeeded: boolean;
  notes: string;
};

type PayerAndPlan = InsurancePayerPlanMap & {
  label: string;
  value: string;
  inNetwork: boolean;
  isReferralNeeded: boolean;
  isOutofStateNeeded: boolean;
  notes: string;
};

export const otherPayer: Plan = {
  label: "I don't see my insurer",
  value: 'Other',
  inNetwork: false,
  isReferralNeeded: false,
  isOutofStateNeeded: false,
  notes: '',
};

export const otherPlan: Plan = {
  label: "I don't see my plan",
  value: 'Other',
  inNetwork: false,
  isReferralNeeded: false,
  isOutofStateNeeded: false,
  notes: '',
};

export type InsuranceAndPolicyHolderFormValues =
  PrimaryPolicyHolderFormValues & {
    carrier: Carrier;
    plan?: Plan;
    insuranceId: string;
  };

enum SelectionOptions {
  InNetwork = 'in-network',
  OutOfNetwork = 'out-of-network',
  DoNotSeePlan = 'do-not-see',
  Unset = 'unset',
}

export const insuranceRequirementHelper = () => (
  <>
    <Typography sx={{ mb: 3 }}>
      Right now, we only see patients whose insurance we accept, and we don’t
      currently have a self-pay option.
    </Typography>

    <Typography>
      If you’re interested in self-pay, or if we don’t take your insurance,
      sites like Zocdoc.com and PsychologyToday.com can help you find care.
    </Typography>
  </>
);

export const visitCostHelper = () => (
  <>
    <Typography sx={{ mb: 3 }}>
      The cost of a visit depends on the details of your insurance coverage. The
      majority of our visits cost patients $30 or less.* Patients who have plans
      with unmet deductibles typically pay more.
    </Typography>

    <Typography sx={{ mb: 3 }}>
      The best way to get an accurate estimate of your cost is to contact your
      insurer directly and ask about outpatient psychiatric services.
    </Typography>

    <Typography>
      *Includes all types of patient costs: copayment, deductible, and
      coinsurance. Excludes no-shows and includes $0 payments.
    </Typography>
  </>
);

const getShortcutToFindingMyPlan = () => (
  <>
    <Typography variant="body2" sx={{ mb: 2 }}>
      If you're a Blue Cross Blue Shield member, type in the first 3 characters
      of your member ID. You can also type in your plan name if you know it.
    </Typography>

    <Box
      component="img"
      alt="Example BCBS insurance card"
      src="/bcbs-insurance-card.png"
      sx={{ borderRadius: 1, maxWidth: '100%', height: 'auto' }}
    />
  </>
);

const InsuranceComboForm = () => {
  const dispatch = useAppDispatch();
  const { addToast } = useToasts();
  const { shouldShowInsuranceDialogBoxes } = useSelector(selectLdFlags);

  // Kameleoon FFs
  const [assessmentInsuranceScreeIconsAndModal] = useExperimentation({
    flagName: 'assessment-insurance-screen-w-icons-and-modal',
    variables: ['assessment-insurance-screen-w-icons-and-modal'],
  });
  const assessmentInsuranceScreenIconsAndModalValue = getFlagStateValue(
    assessmentInsuranceScreeIconsAndModal,
    'assessment-insurance-screen-w-icons-and-modal'
  );

  const [assessmentPayerListFromMappingTableFlag] = useExperimentation({
    flagName: 'assessment-payer-list-from-mapping-table',
    variables: ['assessment-payer-list-from-mapping-table'],
  });
  const assessmentPayerListFromMappingTableValue = getFlagStateValue(
    assessmentPayerListFromMappingTableFlag,
    'assessment-payer-list-from-mapping-table'
  );

  const [carrierSelection, setCarrierSelection] = useState(
    SelectionOptions.Unset
  );

  // New state value for insurance plan set to show policy holder card.
  const [planSelection, setPlanSelection] = useState(SelectionOptions.Unset);
  const [isFormBusy, setIsFormBusy] = useState(false);

  const [isInsuranceInfoModalOpen, setIsInsuranceInfoModalOpen] =
    useState(false);

  const { data: carriersData, loading: carriersLoading } = useGetCarriersQuery({
    skip: assessmentPayerListFromMappingTableValue,
  });
  const allCarriers = carriersData?.getCarriers;
  const transformedCarriers: Carrier[] = allCarriers
    ?.map(({ name }) => ({
      label: name.toLowerCase() === 'other' ? "I don't see my insurer" : name,
      value: name,
    }))
    .sort((a, b) => {
      if (a.value.toLowerCase() === 'other') return -1;
      if (b.value.toLowerCase() === 'other') return 1;
      return 0;
    });

  const [getPlans, { data: plansData, loading: plansLoading }] =
    useGetPlansLazyQuery();
  const allPlans = plansData?.getPlans;
  const plans: Plan[] = allPlans?.map(it => ({
    label: it.plan,
    value: `${it.plan}_${it.inNetwork ? 'in-network' : 'out-of-network'}`,
    inNetwork: it.inNetwork,
    isOutofStateNeeded: it.isOutOfStateNeeded,
    isReferralNeeded: it.isReferralNeeded,
    notes: it.notes,
  }));
  plans?.unshift(otherPlan);

  const { data: insurancePayerData, loading: insurancePayerLoading } =
    useGetInsurancePayersForAssessmentQuery();
  const allInsurancePayerPlanMaps =
    insurancePayerData?.getInsurancePayersForAssessment;

  const sortedAllInsurancePayerPlanMaps = useMemo(() => {
    const sortedPriorityInsurancePayerPlanMaps = allInsurancePayerPlanMaps
      ?.filter(map => map.isPriority)
      .sort((a, b) =>
        a.payerName?.toLowerCase() < b.payerName?.toLowerCase() ? -1 : 1
      );
    const sortedNonpriorityInsurancePayerPlanMaps = allInsurancePayerPlanMaps
      ?.filter(map => !map.isPriority)
      .sort((a, b) =>
        a.payerName?.toLowerCase() < b.payerName?.toLowerCase() ? -1 : 1
      );

    return [
      ...(sortedPriorityInsurancePayerPlanMaps ?? []),
      ...(sortedNonpriorityInsurancePayerPlanMaps ?? []),
    ];
  }, [allInsurancePayerPlanMaps]);

  const sortedInsuranceMapPayers = useMemo(() => {
    const memoPayers = sortedAllInsurancePayerPlanMaps
      .filter(
        (map, index, self) =>
          index ===
          self.findIndex(
            t => t.payerName?.toLowerCase() === map.payerName?.toLowerCase()
          )
      )
      .map(map => ({
        ...map,
        inNetwork: !sortedAllInsurancePayerPlanMaps
          .filter(m => m.payerName === map.payerName)
          .every(m => m.networkStatus === 'OON'),
        isOutofStateNeeded: false,
        isReferralNeeded: false,
        notes: '',
        label: map.payerName,
        value: map.payerName,
      }));

    if (memoPayers.length > 0) memoPayers.unshift({ ...otherPayer, id: -1 });

    return memoPayers;
  }, [sortedAllInsurancePayerPlanMaps]);

  const [
    getInsurancePlansForAssessment,
    { data: insurancePlansData, loading: insurancePlansLoading },
  ] = useGetInsurancePlansForAssessmentLazyQuery();

  const sortedInsuranceMapPlans = useMemo(
    () =>
      insurancePlansData?.getInsurancePlansForAssessment
        .filter(
          (map, index, self) =>
            index ===
            self.findIndex(
              t => t.payerPlans?.toLowerCase() === map.payerPlans?.toLowerCase()
            )
        )
        .map(
          map =>
            ({
              ...map,
              inNetwork: map.networkStatus === 'INN',
              isOutofStateNeeded: false,
              isReferralNeeded: false,
              notes: '',
              label: map.payerPlans,
              value: map.payerPlans,
            } as PayerAndPlan)
        ),
    [insurancePlansData?.getInsurancePlansForAssessment]
  );

  const {
    MI_InsuranceProvider,
    MI_InsurancePlan,
    MI_InsuranceId,
    MI_PrimaryPolicyHolderDetails,
  } = useSelector(selectMedicalInfoAnswers);

  const isGuardian = useSelector(selectIsGuardian);

  let defaultFormCarrier: Carrier | null = null;
  if (assessmentPayerListFromMappingTableValue) {
    defaultFormCarrier = null;
  } else {
    defaultFormCarrier =
      MI_InsuranceProvider?.length > 0
        ? transformedCarriers?.find(c => c.value === MI_InsuranceProvider[0])
        : null;
  }

  const form = useForm<InsuranceAndPolicyHolderFormValues>({
    mode: 'onBlur',
    defaultValues: {
      carrier: defaultFormCarrier,
      plan: null,
      insuranceId:
        MI_InsuranceId && MI_InsuranceId.length > 0 ? MI_InsuranceId[0] : '',
      relationToPatient: MI_PrimaryPolicyHolderDetails?.relationToPatient,
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
    watch,
    setValue,
    trigger,
  } = form;

  useEffect(() => {
    if (
      MI_InsuranceProvider?.length > 0 &&
      plans?.length > 0 &&
      MI_InsurancePlan &&
      !assessmentPayerListFromMappingTableValue
    ) {
      setValue('plan', plans.filter(p => p.value === MI_InsurancePlan[0])[0], {
        shouldValidate: true,
      });
    }
  }, [MI_InsurancePlan, plans?.length > 0]);

  const selectedCarrier = watch('carrier');
  const selectedPlan = watch('plan');
  const relationToPatient = watch('relationToPatient');
  const [isNotPrimaryPolicyHolder, setIsNotPrimaryPolicyHolder] = // for non-minor patients
    useState<boolean>(false);

  const [minorPolicyHolderIsNotGuardian, setMinorPolicyHolderIsNotGuardian] = // for minor patients
    useState<boolean>(false);

  const adultPatientIsNotPolicyHolder = isNotPrimaryPolicyHolder && !isGuardian;
  const minorPolicyHolderIsOther = minorPolicyHolderIsNotGuardian && isGuardian;

  const showPolicyHolderDetailsForm =
    adultPatientIsNotPolicyHolder || minorPolicyHolderIsOther;

  const patientGuardians = useSelector(selectPatientGuardians);

  const saveInsurance = async () => {
    setIsFormBusy(true);
    const providerInput = {
      questionCode: 'MI_InsuranceProvider',
      answerValue: [selectedCarrier.value],
    };

    const planInput = {
      questionCode: 'MI_InsurancePlan',
      answerValue: [selectedPlan?.value],
    };

    const insuranceInput =
      carrierSelection === SelectionOptions.InNetwork
        ? [providerInput, planInput]
        : [providerInput];

    const errors = await dispatch(
      updateInsuranceForm(
        insuranceInput,
        !(carrierSelection === SelectionOptions.InNetwork),
        false
      )
    );
    if (errors) {
      addToast(
        'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
    setIsFormBusy(false);
  };

  useEffect(() => {
    setIsNotPrimaryPolicyHolder(
      !(
        !relationToPatient ||
        relationToPatient === PolicyHolderRelationLabelValueMap.Me
      )
    );

    setMinorPolicyHolderIsNotGuardian(
      !(
        !relationToPatient ||
        relationToPatient !== MinorPolicyHolderRelationLabelValueMap.Other
      )
    );
  }, [relationToPatient]);

  useEffect(() => {
    if (planSelection === SelectionOptions.Unset) {
      return null;
    }
    if (!MI_InsurancePlan || selectedPlan?.value !== MI_InsurancePlan[0]) {
      void saveInsurance();
    }
  }, [planSelection]);

  useEffect(() => {
    if (carrierSelection === SelectionOptions.Unset) {
      return null;
    }
    if (!(carrierSelection === SelectionOptions.InNetwork)) {
      void saveInsurance();
    }
  }, [carrierSelection]);

  useEffect(() => {
    if (selectedCarrier) {
      if (assessmentPayerListFromMappingTableValue) {
        getInsurancePlansForAssessment({
          variables: {
            payerName: selectedCarrier.value,
          },
        });
      } else {
        getPlans({
          variables: {
            carrier: selectedCarrier.value,
          },
        });
      }
    }

    setValue('plan', null, { shouldValidate: true });
    setPlanSelection(SelectionOptions.Unset);

    if (assessmentPayerListFromMappingTableValue) {
      const fullProviderInfo = sortedInsuranceMapPayers.find(
        ({ value }) => value === selectedCarrier?.value
      );

      if (selectedCarrier) {
        if (fullProviderInfo?.inNetwork)
          return setCarrierSelection(SelectionOptions.InNetwork);

        if (fullProviderInfo?.payerName?.toLowerCase() === 'other')
          return setCarrierSelection(SelectionOptions.DoNotSeePlan);

        return setCarrierSelection(SelectionOptions.OutOfNetwork);
      }
    } else {
      const fullProviderInfo = allCarriers?.find(
        ({ name }) => name === selectedCarrier?.value
      );

      if (selectedCarrier) {
        if (fullProviderInfo?.isInNetwork)
          return setCarrierSelection(SelectionOptions.InNetwork);

        if (fullProviderInfo?.name.toLowerCase() === 'other')
          return setCarrierSelection(SelectionOptions.DoNotSeePlan);

        return setCarrierSelection(SelectionOptions.OutOfNetwork);
      }
    }
  }, [selectedCarrier]);

  // On plan selection, determine if plan is in network
  useEffect(() => {
    if (selectedPlan) {
      if (selectedPlan.inNetwork)
        return setPlanSelection(SelectionOptions.InNetwork);

      if (selectedPlan.value.toLowerCase() === 'other')
        return setPlanSelection(SelectionOptions.DoNotSeePlan);

      return setPlanSelection(SelectionOptions.OutOfNetwork);
    }
  }, [selectedPlan]);

  const showContinueButton = (showContinue: boolean): boolean =>
    showContinue ===
    (carrierSelection !== SelectionOptions.DoNotSeePlan &&
      carrierSelection !== SelectionOptions.OutOfNetwork &&
      planSelection !== SelectionOptions.OutOfNetwork);

  const onSubmit: SubmitHandler<
    InsuranceAndPolicyHolderFormValues
  > = async data => {
    try {
      const answer: DispatchUpdateAssessmentInput = [
        {
          questionCode: Screens.MI_InsuranceId,
          answerValue: [data.insuranceId],
        },
      ];

      const formatBirthDate = (date: string) =>
        format(new Date(date), 'yyyy-MM-dd');
      const formattedBirthDate = data.birthDate
        ? formatBirthDate(data.birthDate)
        : undefined;

      const dataToSend: PatientPrimaryPolicyholderInput =
        showPolicyHolderDetailsForm
          ? {
              firstName: data.firstName,
              lastName: data.lastName,
              birthDate: formattedBirthDate,
              relationToPatient: data.relationToPatient,
              isAddressLinkedWithPatient:
                data.isPolicyholderAddressSameAsPatient,
              addressStreet1: data.addressStreet1,
              addressStreet2: data.addressStreet2,
              addressCity: data.addressCity,
              addressState: data.addressState,
              addressZipCode: data.addressZipCode,
              ...(data.customRelation && {
                customRelation: data.customRelation,
              }),
            }
          : createPolicyholderObjectHelper(patientGuardians, relationToPatient);

      await dispatch(
        updatePrimaryPolicyholder(isNotPrimaryPolicyHolder, dataToSend, answer)
      );
    } catch (e) {
      addToast(
        'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
  };

  const filterInsuranceOptions = (options, { inputValue }) =>
    // Always include "I can't find my plan" if it exists
    options.filter(({ value }) => {
      const matchesInput = value
        ?.toLowerCase()
        ?.includes(inputValue?.toLowerCase());
      return matchesInput || value === 'Other';
    });

  const InsurancePlanAutocomplete =
    carrierSelection === SelectionOptions.DoNotSeePlan ||
    carrierSelection === SelectionOptions.OutOfNetwork ? null : (
      <ControlledAutocomplete<InsuranceAndPolicyHolderFormValues>
        name="plan"
        control={control}
        rules={rules.insurancePlan}
        loading={
          assessmentPayerListFromMappingTableValue
            ? insurancePlansLoading
            : plansLoading
        }
        options={
          (assessmentPayerListFromMappingTableValue
            ? [
                { ...otherPlan, id: -1 },
                ...(sortedInsuranceMapPlans?.filter(
                  plan => plan.payerName === selectedCarrier?.value
                ) ?? []),
              ]
            : plans) || []
        }
        filterOptions={filterInsuranceOptions}
        getOptionLabel={option => option.label ?? ''}
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        label={
          assessmentInsuranceScreenIconsAndModalValue
            ? 'Which plan do you have?'
            : 'Choose your insurance plan'
        }
        disabled={
          assessmentInsuranceScreenIconsAndModalValue
            ? !selectedCarrier
            : isFormBusy
        }
        sx={{ mt: 2 }}
      />
    );

  return (
    <div>
      {assessmentInsuranceScreenIconsAndModalValue ? (
        <>
          <InsuranceUsageInfoModal
            isOpen={isInsuranceInfoModalOpen}
            setIsOpen={setIsInsuranceInfoModalOpen}
          />
          <InsuranceLogoGrid />
        </>
      ) : (
        shouldShowInsuranceDialogBoxes && (
          <>
            <DialogBox
              title="Do I have to use insurance?"
              content={insuranceRequirementHelper()}
            />
            <DialogBox
              title="How much will my visit cost?"
              content={visitCostHelper()}
            />
          </>
        )
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <AssessmentCard>
          <Typography component="h3" variant="h5">
            See if we take your plan
          </Typography>

          <ControlledAutocomplete<InsuranceAndPolicyHolderFormValues>
            name="carrier"
            control={control}
            rules={rules.insuranceCarrier}
            loading={
              assessmentPayerListFromMappingTableValue
                ? insurancePayerLoading
                : carriersLoading
            }
            options={
              (assessmentPayerListFromMappingTableValue
                ? sortedInsuranceMapPayers
                : transformedCarriers) || []
            }
            filterOptions={filterInsuranceOptions}
            getOptionLabel={option => option.label ?? ''}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            label={
              assessmentInsuranceScreenIconsAndModalValue
                ? "Who's your insurer?"
                : 'Choose your insurer'
            }
            disabled={isFormBusy}
            sx={{ mt: 2 }}
          />

          {assessmentInsuranceScreenIconsAndModalValue
            ? InsurancePlanAutocomplete
            : selectedCarrier && (
                <Collapse
                  in={carrierSelection === SelectionOptions.InNetwork}
                  unmountOnExit
                  onExited={async () => trigger('carrier')}
                >
                  {InsurancePlanAutocomplete}
                </Collapse>
              )}

          {selectedCarrier && (
            <>
              <Collapse
                in={
                  carrierSelection === SelectionOptions.OutOfNetwork ||
                  planSelection === SelectionOptions.OutOfNetwork
                }
                unmountOnExit
              >
                <Alert severity="info" sx={{ mt: 2 }}>
                  <AlertTitle>Sorry we don't take your insurance</AlertTitle>

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Right now, we can only see patients whose insurance we
                    accept.
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Questions about coverage? Give us a call at (833) 351-8255.
                  </Typography>
                </Alert>
              </Collapse>

              <Collapse
                in={carrierSelection === SelectionOptions.DoNotSeePlan}
                unmountOnExit
              >
                <Alert severity="info" sx={{ mt: 2 }}>
                  <AlertTitle>Don't see your insurer?</AlertTitle>

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Unfortunately that means we don't accept your insurance.
                    We're only able to see patients whose insurance we accept.
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Questions about coverage? Give us a call at (833) 351-8255.
                  </Typography>
                </Alert>
              </Collapse>
            </>
          )}

          {selectedPlan && (
            <>
              <Collapse
                in={
                  planSelection === SelectionOptions.DoNotSeePlan &&
                  carrierSelection !== SelectionOptions.OutOfNetwork
                }
                unmountOnExit
              >
                <Alert severity="info" sx={{ mt: 2 }}>
                  <AlertTitle>Don't see your plan?</AlertTitle>

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    We partner with your insurer, but we might not accept your
                    specific plan. Just upload a picture of your insurance card
                    at the end of the assessment and our team will email you to
                    confirm.
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Questions about coverage? Give us a call at (833) 351-8255.
                  </Typography>
                </Alert>
              </Collapse>

              <Collapse
                in={planSelection === SelectionOptions.InNetwork}
                unmountOnExit
              >
                <Alert severity="success" sx={{ mt: 2 }}>
                  <AlertTitle>We're in-network with your insurance</AlertTitle>
                </Alert>
              </Collapse>
            </>
          )}

          <Collapse
            in={
              selectedCarrier?.value.includes('Blue Cross') ||
              selectedCarrier?.value.includes('Blue Shield')
            }
            unmountOnExit
          >
            <DialogBox
              title="Shortcut to finding my plan"
              content={getShortcutToFindingMyPlan()}
            />
          </Collapse>
        </AssessmentCard>

        {selectedPlan && (
          <Collapse
            in={
              planSelection === SelectionOptions.InNetwork ||
              planSelection === SelectionOptions.DoNotSeePlan
            }
            unmountOnExit
            onExited={async () => trigger('plan')}
          >
            <MoreInsuranceInfoForm form={form} />
          </Collapse>
        )}

        <Collapse
          in={showPolicyHolderDetailsForm}
          unmountOnExit
          onExited={async () => trigger('relationToPatient')}
        >
          <PrimaryPolicyHolderForm form={form} />
        </Collapse>

        {assessmentInsuranceScreenIconsAndModalValue && (
          <Box sx={{ margin: 3 }}>
            <Link
              sx={{ cursor: 'pointer' }}
              onClick={() => setIsInsuranceInfoModalOpen(true)}
            >
              Do I have to use insurance?
            </Link>
          </Box>
        )}

        <Collapse in={showContinueButton(true)} unmountOnExit>
          <AssessmentButtons
            primary={{
              children: 'Continue',
              type: 'submit',
              disabled: !isValid || isSubmitting,
              loading: isSubmitting,
            }}
          />
        </Collapse>

        <Collapse in={showContinueButton(false)} unmountOnExit>
          <AssessmentButtons
            primary={{
              children: 'Return to Talkiatry.com',
              href: 'https://talkiatry.com',
              endIcon: undefined,
            }}
          />
        </Collapse>
      </form>
    </div>
  );
};

export default InsuranceComboForm;
